import type Konva from "konva";
import React from "react";
import { Layer, Line } from "react-konva";
import { LayerType, WallShape } from "../../../types";
import { computeWallFloorPolygons } from "../../../utils/wallFloorFill";
import { getAllWallSegments } from "../../../utils/wallPathSegments";
import { useMapCanvas, useMapCanvasShapes } from "../contexts/MapCanvasContext";

export interface MapCanvasViewLayersProps {
  showOutside?: boolean;
  getShapes: (type: LayerType) => React.ReactNode;
  /** Rendered inside the spots layer, above every shape */
  shapesLayerOverlay?: React.ReactNode;
}

export const MapCanvasViewLayers: React.FC<MapCanvasViewLayersProps> = ({
  showOutside = true,
  getShapes,
  shapesLayerOverlay
}) => {
  const { editingZoneWidth, editingZoneHeight } = useMapCanvas();
  const { shapes } = useMapCanvasShapes();

  // Stabilize wall array reference — only produce a new reference when wall
  // shapes actually change, so the expensive floorFillPolygons computation
  // doesn't rerun when non-wall shapes (desks, spots) are moved.
  // Note: We compare by id and geometry because getAllWallSegments creates
  // new virtual WallShape objects for wall-path edges on every call.
  const wallsRef = React.useRef<WallShape[]>([]);
  const walls = React.useMemo(() => {
    const newWalls = getAllWallSegments(shapes);
    const areEqual =
      newWalls.length === wallsRef.current.length &&
      newWalls.every((w, i) => {
        const prev = wallsRef.current[i];
        return (
          w.id === prev.id &&
          w.x === prev.x &&
          w.y === prev.y &&
          w.x2 === prev.x2 &&
          w.y2 === prev.y2 &&
          w.thickness === prev.thickness
        );
      });
    if (areEqual) {
      return wallsRef.current;
    }
    wallsRef.current = newWalls;
    return newWalls;
  }, [shapes]);

  const floorFillPolygons = React.useMemo(() => {
    if (walls.length < 3) return [];
    return computeWallFloorPolygons(walls);
  }, [walls]);

  // Konva redraws a whole layer when any node on it moves. Zones, walls and
  // furniture live on their own layer so dragging a spot does not repaint
  // every zone fill, dashed border and label (and vice versa). The
  // transformer sits on the top layer and redraws each attached node's own
  // layer, so multi-selection across the two still works.
  const clipToEditingArea = !showOutside
    ? (ctx: Konva.Context) => {
        ctx.beginPath();
        ctx.rect(0, 0, editingZoneWidth, editingZoneHeight);
        ctx.closePath();
      }
    : undefined;

  return (
    <>
      <Layer>
        {floorFillPolygons.map((poly, i) => (
          <Line
            key={`floor-fill-${i}`}
            points={poly.points.flatMap((p) => [p.x, p.y])}
            fill="white"
            closed
            listening={false}
          />
        ))}
        {getShapes("floor")}
        {getShapes("background")}
      </Layer>
      <Layer clipFunc={clipToEditingArea}>{getShapes("shapes")}</Layer>
      <Layer clipFunc={clipToEditingArea}>
        {getShapes("spots")}
        {shapesLayerOverlay}
      </Layer>
    </>
  );
};
